/**
 * Visitor session heartbeat - sends an upsert to `session-heartbeat` while the
 * tab is visible so admins can see who is on the site in near real time.
 *
 * - Respects analytics consent (same gate as track()).
 * - Pauses when the tab is hidden, resumes when visible.
 * - Flushes once on pagehide.
 * - Sends a small payload (path, route, assessment step, UTM/referrer).
 */

import { supabase } from "@/integrations/supabase/client";
import { getConsent } from "@/lib/consent";
import { getAnonymousId, getFirstTouch, getLastTouch } from "@/lib/tracking";
import { getCachedIsAdmin, refreshIsAdmin } from "@/lib/isAdminUser";

const INTERVAL_MS = 45_000;
let intervalId: number | null = null;
let started = false;

function getRoutePattern(path: string): string {
  // Reduce dynamic segments to a stable route shape for grouping.
  return path
    .replace(/\/blog\/[^/]+$/, "/blog/:slug")
    .replace(/\/careers\/[^/]+$/, "/careers/:slug")
    .replace(/\/blueprint\/[^/]+$/, "/blueprint/:slug")
    .replace(/\/micro-quiz\/[^/]+$/, "/micro-quiz/:id")
    .replace(/\/refined-results\/[^/]+$/, "/refined-results/:id")
    .replace(/\/admin\/customers\/[^/]+$/, "/admin/customers/:id")
    .replace(/\/admin\/orders\/[^/]+$/, "/admin/orders/:id");
}

function getAssessmentStep(): number | null {
  if (typeof window === "undefined") return null;
  if (!window.location.pathname.startsWith("/assessment")) return null;
  const q = new URLSearchParams(window.location.search).get("q");
  if (q == null) return 0;
  const n = parseInt(q, 10);
  return Number.isFinite(n) ? n : null;
}

async function sendHeartbeat() {
  if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
  if (getConsent() !== "accepted") return;
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) return;

  try {
    const { data: { user } } = await supabase.auth.getUser();
    // Exclude admins from live analytics - their browsing should not appear
    // as visitor traffic.
    if (user) {
      const isAdmin = await refreshIsAdmin();
      if (isAdmin) return;
    } else if (getCachedIsAdmin()) {
      // Cached admin flag from previous session - skip until cleared on sign out.
      return;
    }
    const ft = (getFirstTouch() ?? {}) as Record<string, unknown>;
    const lt = getLastTouch() as Record<string, unknown>;

    const path = window.location.pathname + window.location.search;
    const body = {
      anonymous_id: getAnonymousId(),
      user_id: user?.id ?? null,
      current_path: path,
      current_route: getRoutePattern(window.location.pathname),
      assessment_step: getAssessmentStep(),
      referrer: document.referrer || null,
      utm_source: (lt.utm_source as string | null) ?? (ft.utm_source as string | null) ?? null,
      utm_medium: (lt.utm_medium as string | null) ?? (ft.utm_medium as string | null) ?? null,
      utm_campaign: (lt.utm_campaign as string | null) ?? (ft.utm_campaign as string | null) ?? null,
      landing_path: (ft.landing_path as string | null) ?? path,
    };

    await supabase.functions.invoke("session-heartbeat", { body });
  } catch {
    // Heartbeats are best-effort - never let them break the app.
  }
}

export function startHeartbeat() {
  if (started || typeof window === "undefined") return;
  started = true;

  const fire = () => void sendHeartbeat();
  fire(); // send immediately on start

  intervalId = window.setInterval(fire, INTERVAL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") fire();
  });

  window.addEventListener("pagehide", () => {
    // Best-effort final beat.
    fire();
  });
}
